/**
 * Длительность из элемента: `duration`, затем `seekable`.
 * `buffered` не используем — для части контейнеров даёт растущую оценку без полной длительности;
 * недостающее добираем через `decodeDurationSecondsFromUrl` в плеере.
 */
export function readReliableDuration(audio: HTMLAudioElement): number {
  const { duration } = audio;
  if (
    Number.isFinite(duration) &&
    duration > 0 &&
    duration !== Number.POSITIVE_INFINITY
  ) {
    return duration;
  }
  try {
    if (audio.seekable.length > 0) {
      const end = audio.seekable.end(audio.seekable.length - 1);
      if (Number.isFinite(end) && end > 0 && end !== Number.POSITIVE_INFINITY) {
        return end;
      }
    }
  } catch {
    // seekable может быть недоступен на ранней стадии
  }
  return 0;
}

export async function decodeDurationSecondsFromUrl(
  url: string,
  signal: AbortSignal
): Promise<number | null> {
  const res = await fetch(url, {
    signal,
    credentials: 'include',
    mode: 'cors',
  });
  if (!res.ok) {
    return null;
  }
  const buf = await res.arrayBuffer();
  const ctx = new AudioContext();
  try {
    const decoded = await ctx.decodeAudioData(buf.slice(0));
    const d = decoded.duration;
    return Number.isFinite(d) && d > 0 ? d : null;
  } finally {
    await ctx.close();
  }
}

export type AudioLoadError = 'network' | 'decode' | 'unavailable';

// MediaError codes from the HTML specification.
const MEDIA_ERR_NETWORK = 2;
const MEDIA_ERR_DECODE = 3;

/**
 * Chrome reports an HTTP error of the source (e.g. a file that is still being
 * processed) as MEDIA_ERR_SRC_NOT_SUPPORTED, so that code stays "unavailable".
 */
export function getAudioLoadError(
  error: Pick<MediaError, 'code'> | null
): AudioLoadError {
  switch (error?.code) {
    case MEDIA_ERR_NETWORK:
      return 'network';
    case MEDIA_ERR_DECODE:
      return 'decode';
    default:
      return 'unavailable';
  }
}
